import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
    DialogDescription,
} from '@/components/ui/dialog';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionType } from '@/features/transactions/types/transactions-dto';
import EDatePicker from '@/components/everridge/EDatePicker';
import ApartmentsLookup from '@/features/apartments/components/ApartmentsLookup';
import Download_04_Icon from '@/components/icons/Download_04_Icon';
import EPopupPicker from '@/components/everridge/EPopupPicker';
import { TransactionsInvoicePopupFormSchema } from '../validators/TransactionsInvoicePopupValidator';
import { useTransactionDownloadInvoicesApi } from '../hooks/useTransactionDownloadInvoicesApi';
import EToast from '@/components/everridge/EToast';

type FormData = z.infer<typeof TransactionsInvoicePopupFormSchema>;

type Props = {
    onClose: () => void;
};

const TransactionsInvoicePopup: FC<Props> = ({ onClose }) => {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();

    const { control, handleSubmit } = useForm<FormData>({
        resolver: zodResolver(TransactionsInvoicePopupFormSchema),
        defaultValues: {
            apartmentId: [],
        },
    });

    const { downloadInvoices, isPendingDownloadInvoices } = useTransactionDownloadInvoicesApi();

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleSubmitFilterForm = async (form: FormData) => {
        try {
            const blob = await downloadInvoices({
                startDate: form.startDate!,
                endDate: form.endDate!,
                apartmentId: form.apartmentId,
                account: form.account ?? null,
            });
            const extensionFromDataType = blob.type.split('/')[1];

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice_${extensionFromDataType}`;
            document.body.appendChild(link);
            link.click();

            window.URL.revokeObjectURL(url);
            link.remove();
        } catch (error) {
            EToast({
                variant: 'error',
                message: `{t('main.transactions.errors.downloadError')}`,
                description: `${error}`,
            });
        } finally {
            handleClose();
        }
    };

    const accountTypes: { name: string; value: TransactionType | null }[] = [
        {
            name: 'main.transactions.type.all',
            value: null,
        },
        {
            name: 'main.transactions.type.property',
            value: TransactionType.Property,
        },
        {
            name: 'main.transactions.type.otherServices',
            value: TransactionType.OtherServices,
        },
    ];

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogDescription />
                    <DialogTitle>
                        {t('elements.components.invoice.title')}
                        <DialogClose className="ml-auto cursor-pointer">
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    <ApartmentsLookup
                        control={control}
                        fieldName={'apartmentId'}
                        selectVariant="multi"
                        label={t('elements.form.apartments.label')}
                        placeholder={t('elements.form.apartments.placeholder')}
                    />
                    <EPopupPicker
                        control={control}
                        fieldName={'account'}
                        label={t('elements.form.account.label')}
                        placeholder={t('elements.form.account.placeholder')}
                        list={accountTypes}
                    />
                    <div className="flex gap-4">
                        <EDatePicker
                            control={control}
                            fieldName={'startDate'}
                            label={t('elements.form.startDate.label')}
                            placeholder={t('elements.form.startDate.placeholder')}
                        />
                        <EDatePicker
                            control={control}
                            fieldName={'endDate'}
                            label={t('elements.form.endDate.label')}
                            placeholder={t('elements.form.endDate.placeholder')}
                        />
                    </div>
                </div>
                <DialogFooter className={'justify-end'}>
                    <Button variant="outline" onClick={handleClose}>
                        {t('elements.buttons.cancel')}
                    </Button>
                    <Button onClick={handleSubmit(handleSubmitFilterForm)}>
                        <Download_04_Icon />
                        {isPendingDownloadInvoices
                            ? t('elements.buttons.downloading')
                            : t('elements.buttons.download')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TransactionsInvoicePopup;
